import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { shareReplay, throttleTime } from 'rxjs/operators';

export interface Experience {
  id: string;
  name: string;
}

export interface DemandSnapshot {
  id: number;
  regionId: number;
  regionName: string;
  driverDemand: number;
  centsPerShift: number;
  expiresAt: string;
  showIfUnprofitable: boolean;
}

export interface Snapshots {
  snapshots: DemandSnapshot[];
}

export interface Location {
  longitude: number;
  latitude: number;
}

export interface Region {
  id: number;
  name: string;
  location: Location;
  timezone: string;
  BeamerSegmentFilter: string;
}

export interface CreateSnapshot {
  regionId: number;
  experienceTypeId: string;
  driverDemand: number;
  centsPerShift: number;
  expiresAt: string;
  showIfUnprofitable: boolean;
  title?: string;
  description?: string;
  loadBonus?: LoadBonus;
}

export interface LoadBonus {
  type: string;
  quantity: number;
  expiresAt: string;
  maxCentsPerUser?: number;
}

@Injectable({
  providedIn: 'root',
})
export class CreateDemandSnapshotService {
  private experienceTypes$$ = new BehaviorSubject<Experience[]>([]);
  private experienceTypesThrottle$$ = new Subject();
  private experienceTypesShared$ = this.experienceTypes$$.pipe(shareReplay(1));

  public get experienceTypes$(): Observable<Experience[]> {
    this.experienceTypesThrottle$$.next();
    return this.experienceTypesShared$;
  }

  private regions$$ = new BehaviorSubject<Region[]>([]);
  private regionsThrottle$$ = new Subject();
  private regionsShared$ = this.regions$$.pipe(shareReplay(1));

  public get regions$(): Observable<Region[]> {
    this.regionsThrottle$$.next();
    return this.regionsShared$;
  }

  constructor(private httpClient: HttpClient) {
    this.experienceTypesThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.getExperienceTypes();
    });
    this.regionsThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.getRegions();
    });
  }

  private getExperienceTypes() {
    this.httpClient
      .get<{ experienceTypes: Experience[] }>(`${environment.api}/v2/internal/sand_experience_types`)
      .subscribe((data) => {
        this.experienceTypes$$.next(data.experienceTypes || ([] as Experience[]));
      });
  }

  private getRegions() {
    this.httpClient.get<{ loHiRegions: Region[] }>(`${environment.api}/v2/internal/regions`).subscribe((data) => {
      this.regions$$.next(data.loHiRegions || ([] as Region[]));
    });
  }

  public getDemandSnapshots$(experienceId: string): Observable<Snapshots> {
    return this.httpClient.get<Snapshots>(`${environment.api}/v2/internal/demand_snapshot/${experienceId}`);
  }

  public createSnapshot(toSend: CreateSnapshot) {
    return this.httpClient.post(`${environment.api}/v2/internal/demand_snapshot`, toSend).toPromise();
  }
}
